import { cn } from '../cn'

type Tone = 'neutral' | 'accent' | 'income' | 'expense' | 'warning' | 'danger' | 'success'

const TONES: Record<Tone, string> = {
  neutral: 'bg-sunken text-ink-muted border-rule',
  accent: 'bg-accent-soft text-accent border-transparent',
  income: 'bg-income-soft text-income border-transparent',
  expense: 'bg-expense-soft text-expense border-transparent',
  warning: 'bg-warning-soft text-warning border-transparent',
  danger: 'bg-danger-soft text-danger border-transparent',
  success: 'bg-success-soft text-success border-transparent',
}

export function Badge({
  tone = 'neutral',
  className,
  ...props
}: React.HTMLAttributes<HTMLSpanElement> & { tone?: Tone }) {
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 rounded-[var(--radius-control)] border px-1.5 py-0.5 text-2xs font-medium',
        TONES[tone],
        className,
      )}
      {...props}
    />
  )
}

/**
 * A user-defined tag. The colour is chosen per tag and stored, so it arrives
 * as a hex value rather than a token; the swatch carries it and the text stays
 * on the sheet's ink so contrast never depends on the user's colour choice.
 */
export function TagChip({
  color,
  label,
  onRemove,
  className,
}: {
  color: string
  label: string
  onRemove?: () => void
  className?: string
}) {
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 rounded-[var(--radius-control)] border border-rule bg-surface py-0.5 pl-1.5 text-2xs font-medium text-ink',
        onRemove ? 'pr-1' : 'pr-1.5',
        className,
      )}
    >
      <span aria-hidden className="size-2 shrink-0 rounded-full" style={{ background: color }} />
      {label}
      {onRemove ? (
        <button
          type="button"
          onClick={onRemove}
          className="rounded-[2px] px-0.5 text-ink-faint hover:text-danger"
          aria-label={`Schlagwort ${label} entfernen`}
        >
          ×
        </button>
      ) : null}
    </span>
  )
}
